import { useCallback, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { useDicomStore } from "../redux/dicomRedux";

/**
 * Build a world↔IJK mapping from dicomBounds + volume dims.
 * Assumes dicomBounds.corners are:
 *  0..3 -> first axial slice (TL, TR, BL, BR)
 *  4..7 -> last  axial slice (TL, TR, BL, BR)
 */
function buildWorldToIJK(dicomBounds, volume) {
  if (!dicomBounds?.corners || !volume) return null;

  const { rows = 1, cols = 1, depth = 1 } = volume;
  const safeRows = Math.max(1, rows);
  const safeCols = Math.max(1, cols);
  const safeDepth = Math.max(1, depth);

  const c = dicomBounds.corners; // 8 entries
  const TL0 = new THREE.Vector3().fromArray(c[0]);
  const TR0 = new THREE.Vector3().fromArray(c[1]);
  const BL0 = new THREE.Vector3().fromArray(c[2]);
  const TL1 = new THREE.Vector3().fromArray(c[4]);

  // Basis along +col (u), +row (v), +slice (w)
  const u = new THREE.Vector3().subVectors(TR0, TL0);
  const v = new THREE.Vector3().subVectors(BL0, TL0);
  const w = new THREE.Vector3().subVectors(TL1, TL0);

  const lenU = u.length();
  const lenV = v.length();
  const lenW = w.length();
  if (!lenU || !lenV || !lenW) return null;

  const uN = u.clone().divideScalar(lenU);
  const vN = v.clone().divideScalar(lenV);
  const wN = w.clone().divideScalar(lenW);

  const sx = lenU / Math.max(1, safeCols - 1);
  const sy = lenV / Math.max(1, safeRows - 1);
  const sz = lenW / Math.max(1, safeDepth - 1);

  const origin = TL0.clone();

  return {
    origin,
    uN,
    vN,
    wN,
    sx,
    sy,
    sz,
    toIJK(worldPoint) {
      const r = new THREE.Vector3().subVectors(worldPoint, origin);
      const i = THREE.MathUtils.clamp(r.dot(uN) / (sx || 1), 0, safeCols - 1);
      const j = THREE.MathUtils.clamp(r.dot(vN) / (sy || 1), 0, safeRows - 1);
      const k = THREE.MathUtils.clamp(r.dot(wN) / (sz || 1), 0, safeDepth - 1);
      return { i, j, k };
    },
  };
}

/**
 * usePointLinking
 * - Click on model → compute IJK in DICOM volume → publish to store
 * - Guards all refs to avoid `.current` crashes if not mounted yet
 */
export default function usePointLinking({
  sceneRef,
  cameraRef,
  containerRef,
  modelRef,
  viewportRef = null,
  onNavigate, // optional: ({ i, j, k, iFloat, jFloat, kFloat, world })
}) {
  const [isPointLinkMode, setIsPointLinkMode] = useState(false);
  const [lastPick, setLastPick] = useState(null);

  const dicomBounds = useDicomStore((s) => s.dicomBounds);
  const volume = useDicomStore((s) => s.volume);

  // these exist in the minimal store below
  const setCrosshairIJK = useDicomStore((s) => s.setCrosshairIJK);
  const setPointLinkingEnabled = useDicomStore((s) => s.setPointLinkingEnabled);

  // reflect mode → store (so DicomViewer only links when ON)
  useEffect(() => {
    setPointLinkingEnabled?.(!!isPointLinkMode);
  }, [isPointLinkMode, setPointLinkingEnabled]);

  const mapping = useMemo(
    () => buildWorldToIJK(dicomBounds, volume),
    [dicomBounds, volume]
  );

  // Raycast into visible meshes
  const pickPointOnModels = useCallback(
    (event) => {
      const container = containerRef?.current;
      const camera = cameraRef?.current;
      const modelRoot = modelRef?.current;
      if (!container || !camera || !modelRoot) return null;

      const rect = container.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      const mouse = new THREE.Vector2(
        ((mouseX - viewport.left) / viewport.width) * 2 - 1,
        -((mouseY - viewport.top) / viewport.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, camera);

      const visibles = modelRoot.children?.filter?.((c) => c.visible) ?? [];
      if (!visibles.length) return null;

      const hit = raycaster.intersectObjects(visibles, true)[0];
      return hit ? hit.point.clone() : null;
    },
    [cameraRef, containerRef, modelRef, viewportRef]
  );

  const handlePick = useCallback(
    (event) => {
      if (!isPointLinkMode) return;
      // ignore clicks that start on UI
      if (event.target?.closest?.(".ui-outside-scene")) return;

      const world = pickPointOnModels(event);
      if (!world || !mapping) return;

      const { i, j, k } = mapping.toIJK(world);
      const ijk = {
        i: Math.round(i),
        j: Math.round(j),
        k: Math.round(k),
        iFloat: i,
        jFloat: j,
        kFloat: k,
      };

      setLastPick({ world: world.clone(), ijk });

      // publish crosshair → DicomViewer triangulates
      setCrosshairIJK?.(ijk);

      // optional callback
      onNavigate?.({ ...ijk, world });
    },
    [isPointLinkMode, mapping, onNavigate, pickPointOnModels, setCrosshairIJK]
  );

  // attach listeners only when container is present
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    const onMouseDown = (e) => {
      if (!isPointLinkMode) return;
      if (e.button !== 0) return;
      // (no drag detection needed — single pick like Annotations)
    };
    const onMouseUp = (e) => {
      if (!isPointLinkMode) return;
      if (e.button !== 0) return;
      handlePick(e);
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mouseup", onMouseUp);
    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mouseup", onMouseUp);
    };
  }, [containerRef, handlePick, isPointLinkMode]);

  return { isPointLinkMode, setIsPointLinkMode, lastPick };
}
